import { fromIsoDate, startOfLocalDay, toIsoDate } from '@/app/pages/monitoring/molecules/create-monitoring-brief/sections/monitoring-window/monitoring-window.helpers';
import { ResultsStat } from '../mon-results/results-scan';
import { MonitorCreator } from '../mon-shell/brief-creators';

export type OverviewMetric = 'views' | 'engagement' | 'posts';
export type OverviewFormat = 'all' | PostFormat;
export type PostFormat = 'tiktok' | 'reel' | 'story' | 'post';
export type OverviewPeriod = '7' | '30' | 'all';

export interface OverviewQuery {
  metric: OverviewMetric;
  format: OverviewFormat;
  period: OverviewPeriod;
}

export const OVERVIEW_METRICS: readonly { id: OverviewMetric; label: string }[] = [
  { id: 'views', label: 'Views' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'posts', label: 'Posts found' },
];

export const OVERVIEW_FORMATS: readonly { id: OverviewFormat; label: string }[] = [
  { id: 'all', label: 'All formats' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'reel', label: 'Reels' },
  { id: 'story', label: 'Stories' },
  { id: 'post', label: 'Posts' },
];

export const OVERVIEW_PERIODS: readonly { id: OverviewPeriod; label: string }[] = [
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: 'all', label: 'Campaign to date' },
];

export const FORMAT_LABEL: Record<PostFormat, string> = {
  tiktok: 'TikTok',
  reel: 'Reel',
  story: 'Story',
  post: 'Post',
};

/**
 * Each creator keeps the same colour wherever they appear. Mid-tones, so a dot or avatar reads on light and dark
 * cards alike; the handover's near-black placeholders disappeared on the dark theme.
 */
const CREATOR_COLORS = ['#DB24BF', '#8303F8', '#0F766E', '#95159E', '#6508FB', '#C2410C', '#1D4ED8', '#BE185D'];

const TOP_POSTS = 5;
const DEFAULT_QUERY: OverviewQuery = { metric: 'views', format: 'all', period: '30' };
const DAY_LABEL = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const LONG_DAY_LABEL = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export type OverviewDelta =
  | { kind: 'change'; percent: number; up: boolean; days: number }
  | { kind: 'since'; label: string }
  | { kind: 'no-earlier' }
  | { kind: 'no-data' };

export interface MetricTab {
  id: OverviewMetric;
  label: string;
  value: number;
  valueText: string;
  delta: OverviewDelta;
}

export interface ChartPost {
  postId: string;
  creatorName: string;
  color: string;
  formatLabel: string;
}

export interface ChartDay {
  iso: string;
  label: string;
  longLabel: string;
  value: number;
  posts: ChartPost[];
}

export interface OverviewGlance {
  posted: number;
  hired: number;
  notPosted: number;
  progress: number;
  averageEngagementRate: number;
  likes: number;
  comments: number;
  sharesAndSaves: number;
  storyViews: number;
}

export interface LeaderRow {
  creatorId: string;
  name: string;
  initial: string;
  handle: string;
  followers: number;
  photoUrl: string | null;
  color: string;
  value: number;
  valueText: string;
  posts: number;
  share: number;
}

export interface TopPost {
  rank: number;
  post: ResultsStat;
  format: PostFormat;
  formatLabel: string;
  color: string;
}

export interface OverviewScan {
  status: 'empty' | 'live';
  query: OverviewQuery;
  metricLabel: string;
  periodLabel: string;
  tabs: MetricTab[];
  days: ChartDay[];
  glance: OverviewGlance;
  leaders: LeaderRow[];
  waiting: LeaderRow[];
  topMetricLabel: string;
  top: TopPost[];
  /** Every post in the current format, by views. The post drawer steps through this list. */
  drawerList: ResultsStat[];
  statsUpdatedLabel: string | null;
}

export interface OverviewInput {
  posts: readonly ResultsStat[];
  creators: readonly MonitorCreator[];
  query: OverviewQuery;
  campaignStart: Date | null;
  now: Date;
}

export function parseOverviewQuery(params: { get(name: string): string | null }): OverviewQuery {
  const metric = params.get('metric');
  const format = params.get('format');
  const period = params.get('period');
  return {
    metric: OVERVIEW_METRICS.some((item) => item.id === metric) ? (metric as OverviewMetric) : DEFAULT_QUERY.metric,
    format: OVERVIEW_FORMATS.some((item) => item.id === format) ? (format as OverviewFormat) : DEFAULT_QUERY.format,
    period: OVERVIEW_PERIODS.some((item) => item.id === period) ? (period as OverviewPeriod) : DEFAULT_QUERY.period,
  };
}

export function overviewQueryParams(query: OverviewQuery): Record<string, string | null> {
  return {
    metric: query.metric === DEFAULT_QUERY.metric ? null : query.metric,
    format: query.format === DEFAULT_QUERY.format ? null : query.format,
    period: query.period === DEFAULT_QUERY.period ? null : query.period,
  };
}

/** Uses the stored post type, then the URL, then the platform. Instagram posts with neither are feed posts. */
export function postFormat(post: Pick<ResultsStat, 'postType' | 'platform' | 'postUrl'>): PostFormat {
  const type = post.postType.toLowerCase();
  if (type === 'tiktok' || post.platform.toLowerCase() === 'tiktok') {
    return 'tiktok';
  }
  if (type === 'reel' || type === 'igtv') {
    return 'reel';
  }
  if (type === 'story') {
    return 'story';
  }
  if (!type && post.postUrl.includes('/reel/')) {
    return 'reel';
  }
  if (!type && post.postUrl.includes('/stories/')) {
    return 'story';
  }
  return 'post';
}

/** 950 → 950, 1,240 → 1.2k, 142,000 → 142k, 1,200,000 → 1.2M. */
export function compactNumber(value: number): string {
  const rounded = Math.round(value);
  if (rounded >= 1e6) {
    return `${trimDecimal(rounded / 1e6)}M`;
  }
  if (rounded >= 1e5) {
    return `${Math.round(rounded / 1e3)}k`;
  }
  if (rounded >= 1e3) {
    return `${trimDecimal(rounded / 1e3)}k`;
  }
  return String(rounded);
}

export function creatorColor(creatorId: string): string {
  let hash = 0;
  for (let index = 0; index < creatorId.length; index++) {
    hash = (hash * 31 + creatorId.charCodeAt(index)) | 0;
  }
  return CREATOR_COLORS[Math.abs(hash) % CREATOR_COLORS.length];
}

export function metricLabel(metric: OverviewMetric): string {
  return OVERVIEW_METRICS.find((item) => item.id === metric)?.label ?? 'Views';
}

/**
 * Builds the Overview from the latest stats per post. There is no daily history yet, so a post's whole total
 * counts on the day it went live: the chart reads as "from posts that went live that day".
 */
export function scanOverview(input: OverviewInput): OverviewScan {
  const { query } = input;
  const all = input.posts.filter((post) => post.socialPostId.trim().length > 0);
  const inFormat = all.filter((post) => query.format === 'all' || postFormat(post) === query.format);
  const range = periodRange(query.period, all, input.campaignStart, input.now);
  const inPeriod = inFormat.filter((post) => withinRange(post.postedIso, range.from, range.to));
  const live = all.length > 0;
  const periodLabel = OVERVIEW_PERIODS.find((item) => item.id === query.period)?.label ?? '';
  const topMetric: Exclude<OverviewMetric, 'posts'> = query.metric === 'posts' ? 'views' : query.metric;

  const tabs = OVERVIEW_METRICS.map((metric): MetricTab => {
    const value = total(inPeriod, metric.id);
    return {
      id: metric.id,
      label: metric.label,
      value,
      valueText: compactNumber(value),
      delta: live ? delta(inFormat, metric.id, range) : { kind: 'no-data' },
    };
  });

  const { leaders, waiting, posted } = leaderboard(all, inPeriod, input.creators, query.metric);
  const hired = Math.max(input.creators.length, posted);
  const stories = inPeriod.filter((post) => postFormat(post) === 'story');

  return {
    status: live ? 'live' : 'empty',
    query,
    metricLabel: metricLabel(query.metric),
    periodLabel,
    tabs,
    days: chartDays(range, inPeriod, query.metric),
    glance: {
      posted,
      hired,
      notPosted: hired - posted,
      progress: hired > 0 ? (posted / hired) * 100 : 0,
      averageEngagementRate: inPeriod.length ? inPeriod.reduce((sum, post) => sum + post.engagementRate, 0) / inPeriod.length : 0,
      likes: sum(inPeriod, (post) => post.likes),
      comments: sum(inPeriod, (post) => post.comments),
      sharesAndSaves: sum(inPeriod, (post) => post.shares + post.saves),
      storyViews: sum(stories, (post) => post.views),
    },
    leaders,
    waiting,
    topMetricLabel: metricLabel(topMetric),
    top: [...inFormat]
      .sort((left, right) => right[topMetric] - left[topMetric] || right.views - left.views || left.socialPostId.localeCompare(right.socialPostId))
      .slice(0, TOP_POSTS)
      .map((post, index) => {
        const format = postFormat(post);
        return { rank: index + 1, post, format, formatLabel: FORMAT_LABEL[format], color: creatorColor(creatorKey(post)) };
      }),
    drawerList: [...inFormat].sort((left, right) => right.views - left.views || left.socialPostId.localeCompare(right.socialPostId)),
    statsUpdatedLabel: statsUpdatedLabel(all, input.now),
  };
}

interface PeriodRange {
  from: Date;
  to: Date;
  days: number;
  previous: { from: Date; to: Date } | null;
}

/**
 * Last 7 and 30 days end today and compare with the same number of days before them.
 * Campaign to date starts on the monitoring start, or on the first post if that came earlier.
 */
function periodRange(period: OverviewPeriod, posts: readonly ResultsStat[], campaignStart: Date | null, now: Date): PeriodRange {
  const today = startOfLocalDay(now);
  if (period === 'all') {
    const firstPost = posts
      .map((post) => fromIsoDate(post.postedIso))
      .filter((date): date is Date => date !== null)
      .reduce<Date | null>((earliest, date) => (!earliest || date < earliest ? date : earliest), null);
    const starts = [campaignStart ? startOfLocalDay(campaignStart) : null, firstPost].filter((date): date is Date => date !== null);
    const from = starts.length ? new Date(Math.min(...starts.map((date) => date.getTime()))) : today;
    const start = from > today ? today : from;
    return { from: start, to: today, days: dayCount(start, today), previous: null };
  }
  const days = Number(period);
  const from = shiftDays(today, -(days - 1));
  return { from, to: today, days, previous: { from: shiftDays(from, -days), to: shiftDays(from, -1) } };
}

function delta(posts: readonly ResultsStat[], metric: OverviewMetric, range: PeriodRange): OverviewDelta {
  if (!range.previous) {
    return { kind: 'since', label: DAY_LABEL.format(range.from) };
  }
  const { from, to } = range.previous;
  const before = total(posts.filter((post) => withinRange(post.postedIso, from, to)), metric);
  if (before === 0) {
    return { kind: 'no-earlier' };
  }
  const now = total(posts.filter((post) => withinRange(post.postedIso, range.from, range.to)), metric);
  return { kind: 'change', percent: Math.round((Math.abs(now - before) / before) * 100), up: now >= before, days: range.days };
}

function chartDays(range: PeriodRange, posts: readonly ResultsStat[], metric: OverviewMetric): ChartDay[] {
  const byDay = new Map<string, ResultsStat[]>();
  for (const post of posts) {
    const list = byDay.get(post.postedIso) ?? [];
    list.push(post);
    byDay.set(post.postedIso, list);
  }
  const days: ChartDay[] = [];
  for (let day = range.from; day <= range.to; day = shiftDays(day, 1)) {
    const iso = toIsoDate(day);
    const live = byDay.get(iso) ?? [];
    days.push({
      iso,
      label: DAY_LABEL.format(day),
      longLabel: LONG_DAY_LABEL.format(day),
      value: total(live, metric),
      posts: live.map((post) => ({
        postId: post.socialPostId,
        creatorName: post.creatorName || post.handle || 'A creator',
        color: creatorColor(creatorKey(post)),
        formatLabel: FORMAT_LABEL[postFormat(post)],
      })),
    });
  }
  return days;
}

/**
 * Creators with a post rank by the metric in the period and format; the rest wait under "Not posted yet".
 * A post whose creator isn't on the brief's list (a handle we couldn't match) still ranks, under its handle.
 */
function leaderboard(
  all: readonly ResultsStat[],
  inPeriod: readonly ResultsStat[],
  creators: readonly MonitorCreator[],
  metric: OverviewMetric,
): { leaders: LeaderRow[]; waiting: LeaderRow[]; posted: number } {
  const rows = new Map<string, LeaderRow>();
  for (const creator of creators) {
    rows.set(creator.id, leaderRow(creator.id, creator.name, creator.handle, creator.followers, creator.photoUrl, creator.posts));
  }

  const postCounts = new Map<string, number>();
  for (const post of all) {
    const key = creatorKey(post);
    postCounts.set(key, (postCounts.get(key) ?? 0) + 1);
    if (!rows.has(key)) {
      rows.set(key, leaderRow(key, post.creatorName || post.handle || 'Creator', post.handle, post.followers, null, 0));
    }
  }
  for (const [key, count] of postCounts) {
    const row = rows.get(key);
    if (row) {
      row.posts = Math.max(row.posts, count);
    }
  }
  for (const post of inPeriod) {
    const row = rows.get(creatorKey(post));
    if (row) {
      row.value += metricValue(post, metric);
    }
  }

  const leaders = [...rows.values()]
    .filter((row) => row.posts > 0)
    .sort((left, right) => right.value - left.value || left.name.localeCompare(right.name));
  const top = Math.max(1, ...leaders.map((row) => row.value));
  for (const row of leaders) {
    row.share = (row.value / top) * 100;
    row.valueText = compactNumber(row.value);
  }
  const waiting = [...rows.values()].filter((row) => row.posts === 0);
  return { leaders, waiting, posted: leaders.length };
}

function leaderRow(id: string, name: string, handle: string, followers: number, photoUrl: string | null, posts: number): LeaderRow {
  return {
    creatorId: id,
    name,
    initial: (name.replace(/^@/, '').trim()[0] ?? '?').toUpperCase(),
    handle,
    followers,
    photoUrl,
    color: creatorColor(id),
    value: 0,
    valueText: '0',
    posts,
    share: 0,
  };
}

function statsUpdatedLabel(posts: readonly ResultsStat[], now: Date): string | null {
  const latest = posts.reduce((max, post) => {
    const time = post.statsUpdatedAt ? new Date(post.statsUpdatedAt).getTime() : NaN;
    return Number.isFinite(time) && time > max ? time : max;
  }, 0);
  if (!latest) {
    return null;
  }
  const minutes = Math.max(0, Math.floor((now.getTime() - latest) / 60000));
  if (minutes < 1) {
    return 'Stats updated just now';
  }
  if (minutes < 60) {
    return `Stats updated ${plural(minutes, 'minute')} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `Stats updated ${plural(hours, 'hour')} ago`;
  }
  return `Stats updated ${plural(Math.floor(hours / 24), 'day')} ago`;
}

function creatorKey(post: ResultsStat): string {
  return post.creatorId || `handle:${post.handle.toLowerCase()}`;
}

function metricValue(post: ResultsStat, metric: OverviewMetric): number {
  return metric === 'posts' ? 1 : post[metric];
}

function total(posts: readonly ResultsStat[], metric: OverviewMetric): number {
  return sum(posts, (post) => metricValue(post, metric));
}

function sum(posts: readonly ResultsStat[], pick: (post: ResultsStat) => number): number {
  return posts.reduce((running, post) => running + pick(post), 0);
}

function withinRange(iso: string, from: Date, to: Date): boolean {
  const day = fromIsoDate(iso);
  return !!day && day >= from && day <= to;
}

function shiftDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function dayCount(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
}

function trimDecimal(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '');
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
